import { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Plus, Trash2, Pencil, MessageSquare, Sparkles, GraduationCap, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { metaFor } from "@/lib/models";
import api, { formatError } from "@/lib/api";

const EMPTY = { nome: "", area: "", tom: "", objetivos: "", projeto: "", modelos: [], modelo_default: "claude-opus-5-5", modelo_imagem: "gpt-image-1" };

export default function AgentesPage() {
  const navigate = useNavigate();
  const { reloadConversas } = useOutletContext();
  const [agentes, setAgentes] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([api.get("/agentes"), api.get("/models")]);
      setAgentes(a.data);
      setModels(m.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const abrirNovo = () => { setForm({ ...EMPTY, modelos: ["claude-opus-5-5"] }); setEditId(null); setOpen(true); };
  const abrirEdit = (a) => {
    setForm({ nome: a.nome, area: a.area, tom: a.tom, objetivos: a.objetivos, projeto: a.projeto, modelos: a.modelos, modelo_default: a.modelo_default, modelo_imagem: a.modelo_imagem || "gpt-image-1" });
    setEditId(a.id); setOpen(true);
  };

  const toggleModelo = (id) => {
    setForm((f) => {
      const has = f.modelos.includes(id);
      const modelos = has ? f.modelos.filter((x) => x !== id) : [...f.modelos, id];
      const modelo_default = modelos.includes(f.modelo_default) ? f.modelo_default : (modelos[0] || "");
      return { ...f, modelos, modelo_default };
    });
  };

  const guardar = async () => {
    if (!form.nome.trim()) { toast.error("Dá um nome ao agente"); return; }
    if (form.modelos.length === 0) { toast.error("Escolhe pelo menos uma inteligência"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/agentes/${editId}`, form);
      else await api.post("/agentes", form);
      toast.success(editId ? "Agente atualizado" : "Agente criado");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const apagar = async (id) => {
    await api.delete(`/agentes/${id}`);
    setAgentes((a) => a.filter((x) => x.id !== id));
    toast.success("Agente apagado");
  };

  const conversar = async (a) => {
    const { data } = await api.post("/conversas", { agente_id: a.id });
    await reloadConversas();
    navigate(`/c/${data.id}`);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Bot className="text-emerald-400" /> Agentes</h1>
            <p className="text-sm text-slate-400 mt-1">Personas especializadas que usam a memória partilhada e evoluem a cada conversa que guardas.</p>
          </div>
          <button data-testid="agente-novo-button" onClick={abrirNovo} className="px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors">
            <Plus size={16} /> Novo agente
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-600" /></div>
        ) : agentes.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <Bot size={40} className="mx-auto mb-3 opacity-40" />
            <p>Ainda não tens agentes. Cria o teu primeiro especialista.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {agentes.map((a) => (
              <div key={a.id} data-testid="agente-card" className="p-5 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold truncate">{a.nome}</h3>
                    <p className="text-xs text-emerald-400/90 font-mono">{a.area || "generalista"}{a.projeto ? ` · ${a.projeto}` : ""}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid="agente-edit" onClick={() => abrirEdit(a)} className="p-2 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800"><Pencil size={14} /></button>
                    <button data-testid="agente-delete" onClick={() => apagar(a.id)} className="p-2 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-slate-800"><Trash2 size={14} /></button>
                  </div>
                </div>
                {a.objetivos && <p className="text-sm text-slate-300 mt-2 line-clamp-2">{a.objetivos}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {a.modelos.map((m) => (
                    <span key={m} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
                      {metaFor(m).short}{m === a.modelo_default ? " ★" : ""}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span data-testid="agente-nivel" className="text-xs font-mono text-amber-300/90 flex items-center gap-1">
                    <GraduationCap size={13} /> Nível {a.nivel}
                  </span>
                  <button data-testid="agente-conversar" onClick={() => conversar(a)} className="px-3.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm font-semibold flex items-center gap-2 transition-colors">
                    <MessageSquare size={14} /> Conversar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agente-dialog" className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles size={18} className="text-emerald-400" /> {editId ? "Editar agente" : "Novo agente"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nome" testid="agente-nome"><input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Dra. Vitória" className={inputCls} data-testid="agente-nome" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Área"><input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Direito comercial" className={inputCls} data-testid="agente-area" /></Field>
              <Field label="Projeto"><input value={form.projeto} onChange={(e) => setForm({ ...form, projeto: e.target.value })} placeholder="Nimbus" className={inputCls} data-testid="agente-projeto" /></Field>
            </div>
            <Field label="Tom / estilo"><input value={form.tom} onChange={(e) => setForm({ ...form, tom: e.target.value })} placeholder="Assertivo, confiante, nunca perde um caso" className={inputCls} data-testid="agente-tom" /></Field>
            <Field label="Objetivos"><textarea value={form.objetivos} onChange={(e) => setForm({ ...form, objetivos: e.target.value })} rows={3} placeholder="Proteger o cliente e ganhar sempre" className={`${inputCls} resize-none`} data-testid="agente-objetivos" /></Field>
            <div>
              <p className="text-xs text-slate-400 mb-2">Inteligências que este agente pode usar (★ = por defeito)</p>
              <div className="space-y-1.5">
                {models.map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-800">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.modelos.includes(m.id)} onChange={() => toggleModelo(m.id)} data-testid={`agente-modelo-${m.id}`} className="accent-emerald-500" />
                      {m.label}
                    </label>
                    {form.modelos.includes(m.id) && (
                      <button onClick={() => setForm({ ...form, modelo_default: m.id })} className={`text-[11px] px-2 py-0.5 rounded-full font-mono ${form.modelo_default === m.id ? "bg-emerald-500 text-slate-950" : "bg-slate-800 text-slate-400 hover:text-slate-200"}`}>
                        {form.modelo_default === m.id ? "★ padrão" : "definir padrão"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Modelo de imagem (para gerar imagens/catálogos)</label>
              <select
                data-testid="agente-modelo-imagem"
                value={form.modelo_imagem}
                onChange={(e) => setForm({ ...form, modelo_imagem: e.target.value })}
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/50"
              >
                <option value="gpt-image-1">GPT Image 1 (OpenAI)</option>
                <option value="nano-banana-pro">Nano Banana Pro (Gemini — requer Gemini ativo)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-400 hover:text-slate-200 text-sm">Cancelar</button>
            <button data-testid="agente-guardar-button" onClick={guardar} disabled={saving} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
              {saving && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/50";
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
